/**
 * Tradução: respostas do onboarding → payload do `generateCompletePlan`.
 *
 * O onboarding e o "Gerador de plano" da aba Treino perguntam quase a mesma
 * coisa com nomes diferentes. Este módulo é o ÚNICO lugar que sabe as duas
 * grafias — sem ele, o mapeamento se espalharia pelo orquestrador e por
 * quem quer que dispare uma regeneração depois.
 *
 * Nada aqui inventa dado: campo sem resposta sai com o mesmo default que o
 * `GeradorPlanoCompleto` usaria. Quem quiser afinar (equipamento item a
 * item, objetivos secundários, modalidades) continua tendo o gerador
 * completo na aba Treino.
 *
 * Módulo puro. Tem teste.
 */

/** Ids do catálogo de equipamento do gerador, por local de treino. */
const EQUIPAMENTO_POR_LOCAL = {
  // Academia: o mesmo preset "padrão" do gerador (tudo que é comum numa
  // academia de bairro). A lista é literal de propósito — importar o
  // catálogo do gerador acoplaria o onboarding a um componente de tela.
  academia: [
    'barra', 'haltere', 'polia_alta', 'polia_baixa', 'pulley_assento',
    'banco_reto', 'banco_inclinado', 'banco_hiperextensao', 'banco_rosca_scott',
    'squat_rack', 'barra_fixa', 'barra_w', 'barra_t', 'triangulo',
    'leg_press', 'hack_squat', 'cadeira_extensora', 'mesa_flexora',
    'abdutor_adutor', 'maquina_supino', 'smith', 'desenv_articulado',
    'maquina_remada', 'gravitron', 'maquina_abdominal',
    'esteira', 'bicicleta', 'eliptico', 'peso_corpo',
  ],
  casa: ['haltere', 'kettlebell', 'banco_reto', 'elastico', 'corda_pular', 'peso_corpo'],
  peso_corpo: ['peso_corpo', 'elastico', 'corda_pular'],
};

/** Nível do onboarding (ACTIVITY_LEVEL) → condicionamento do gerador. */
const CONDICIONAMENTO = {
  'muito baixo': 'iniciante',
  baixo: 'iniciante',
  medio: 'intermediario',
  alto: 'avancado',
};

/**
 * Monta o `respostas` que o `generateCompletePlan` espera.
 *
 * @param {object} answers respostas cruas do onboarding
 * @returns {object} payload do gerador
 */
export function respostasDaGeracao(answers = {}) {
  const dias = Array.isArray(answers.TRAINING_DAYS) ? answers.TRAINING_DAYS : [];
  const local = answers.TRAINING_PLACE || 'academia';

  /* O gerador manda `restricoes` e `preferencias` como TEXTO livre para o
     prompt. O onboarding já produz texto nesses dois campos (ver
     ChipsComOutro): aqui é só reencaminhar, juntando a lesão declarada — que
     no gerador é a pergunta "limitação física ou lesão atual". */
  const limitacao = answers.SPECIAL_CARE_DETAILS?.trim();
  const restricaoAlimentar = answers.RESTRICAO_ALIMENTAR_DETAILS?.trim();

  return {
    // Limitação física / lesão — a pergunta `restricoes` do gerador.
    restricoes: limitacao || 'Nenhuma',
    restricoes_descricao: limitacao || '',

    // Preferência de treino: o onboarding não pergunta modalidade, mas
    // pergunta o local — que é o que muda o treino de verdade.
    preferencias: textoDePreferencias(answers),

    experienciaUnidade: 'meses',
    experienciaValor: '',
    modalidadesAnteriores: '',

    condicionamento: CONDICIONAMENTO[answers.ACTIVITY_LEVEL] || 'iniciante',

    frequencia_dias: dias,
    frequencia_qtd: dias.length,

    tempoSessao: answers.SESSION_TIME || 'normal - De 40 a 60 minutos',

    localTreino_lista: [local],
    localTreino_outros_desc: '',

    objetivos_secundarios: [],
    equipamentos_disponiveis:
      EQUIPAMENTO_POR_LOCAL[local] || EQUIPAMENTO_POR_LOCAL.academia,

    // Campos que só o onboarding coleta e que o prompt de dieta aproveita.
    // Nomes novos: o payload é um blob que a CF repassa, não um contrato
    // posicional.
    refeicoes_do_dia: Array.isArray(answers.MEALS_PER_DAY) ? answers.MEALS_PER_DAY : [],
    qtd_refeicoes: Array.isArray(answers.MEALS_PER_DAY) && answers.MEALS_PER_DAY.length
      ? answers.MEALS_PER_DAY.length
      : null,
    restricao_alimentar: restricaoAlimentar || '',
    foco_muscular: Array.isArray(answers.INTEREST) ? answers.INTEREST : [],
    uso_medicamento_emagrecedor: answers.WEIGHT_LOSS_MEDICATION || 'nao_informado',
  };
}

/** O texto de "preferência de treino" que vai para o prompt. */
function textoDePreferencias(answers) {
  const partes = [];
  const local = {
    academia: 'Treino em academia completa',
    casa: 'Treino em casa, com halteres e elásticos',
    peso_corpo: 'Treino só com o peso do corpo',
  }[answers.TRAINING_PLACE];
  if (local) partes.push(local);

  const foco = Array.isArray(answers.INTEREST) ? answers.INTEREST : [];
  if (foco.length) partes.push(`Prioridade para: ${foco.join(', ')}`);

  const dieta = answers.PREFERENCIA_ALIMENTAR_DETAILS?.trim();
  if (dieta) partes.push(`Estilo alimentar: ${dieta}`);

  return partes.join('. ');
}

export default respostasDaGeracao;
